import React, {useMemo} from 'react';
import {useCurrentFrame, useVideoConfig} from 'remotion';
import type {HighlightItem} from '../../templates/custom/video-highlight/types';
import {
  getFocusAnimationProgressAtTime,
  resolveMarkerRect,
} from './marker-utils';

export interface FocusAreaMarkerProps {
  highlight: HighlightItem | null;
  children: React.ReactNode;
  defaultFadeIn?: number;
  defaultFadeOut?: number;
  currentTimeInSeconds?: number;
  coordinateWidth?: number | null;
  coordinateHeight?: number | null;
}

export const FocusAreaMarker: React.FC<FocusAreaMarkerProps> = ({
  highlight,
  children,
  defaultFadeIn = 0,
  defaultFadeOut = 0,
  currentTimeInSeconds,
  coordinateWidth,
  coordinateHeight,
}) => {
  const frame = useCurrentFrame();
  const {fps, width: compositionWidth, height: compositionHeight} =
    useVideoConfig();
  const effectiveCurrentTimeInSeconds =
    currentTimeInSeconds ?? frame / fps;

  const animationProgress = useMemo(() => {
    if (!highlight) {
      return 0;
    }

    return getFocusAnimationProgressAtTime({
      highlight,
      currentTimeInSeconds: effectiveCurrentTimeInSeconds,
      defaultFadeIn,
      defaultFadeOut,
    });
  }, [
    defaultFadeIn,
    defaultFadeOut,
    effectiveCurrentTimeInSeconds,
    highlight,
  ]);

  const resolvedRect = useMemo(() => {
    if (!highlight || highlight.position == null) {
      return null;
    }

    return resolveMarkerRect({
      position: highlight.position,
      compositionWidth,
      compositionHeight,
      coordinateWidth,
      coordinateHeight,
    });
  }, [
    compositionHeight,
    compositionWidth,
    coordinateHeight,
    coordinateWidth,
    highlight,
  ]);

  const mediaStyle = useMemo(() => {
    if (!highlight || !resolvedRect || animationProgress <= 0) {
      return {
        position: 'absolute',
        inset: 0,
        transform: 'none',
      } satisfies React.CSSProperties;
    }

    const focusCenterX = resolvedRect.x + resolvedRect.width / 2;
    const focusCenterY = resolvedRect.y + resolvedRect.height / 2;
    // Scale needed for the focus region to fully cover the screen.
    const fillScreenScale = Math.max(
      compositionWidth / resolvedRect.width,
      compositionHeight / resolvedRect.height,
    );
    const hasManualZoom =
      highlight.autoFitZoom === false &&
      highlight.zoomScale != null &&
      highlight.zoomScale > 1;
    // Default behaviour for focus-area is to fill the selected area to the
    // screen. A manual zoom only applies when autoFitZoom is explicitly false.
    const targetZoomScale = hasManualZoom
      ? (highlight.zoomScale as number)
      : fillScreenScale;
    const shouldMoveFocusToCenter = targetZoomScale > 1;
    const transformProgress = shouldMoveFocusToCenter ? animationProgress : 0;
    const scale = 1 + (targetZoomScale - 1) * transformProgress;
    const canvasCenterX = compositionWidth / 2;
    const canvasCenterY = compositionHeight / 2;
    const offsetX = (canvasCenterX - focusCenterX) * transformProgress;
    const offsetY = (canvasCenterY - focusCenterY) * transformProgress;

    return {
      position: 'absolute',
      inset: 0,
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: `${focusCenterX}px ${focusCenterY}px`,
    } satisfies React.CSSProperties;
  }, [
    animationProgress,
    compositionHeight,
    compositionWidth,
    highlight,
    resolvedRect,
  ]);

  return <div style={mediaStyle}>{children}</div>;
};
